/**
 * Map sky score docs to four slots by round number (1–4), not by sort order.
 * Missing rounds stay 0 / '' so round 4 never shifts into the R3 column.
 * @param {Array<{teamId?: string, type?: string, round?: number, value?: number, busts?: number}>} teamScoreDocs
 * @returns {{ numericSkyScores: number[], skyScores: string[] }}
 */
export function skyRoundSlotsFromTeamScores(teamScoreDocs) {
  const sky = teamScoreDocs.filter((s) => s.type !== 'bonus');
  const byRound = new Map();
  for (const s of sky) {
    const r = Number(s.round);
    if (r >= 1 && r <= 4) {
      byRound.set(r, s);
    }
  }
  const numericSkyScores = [1, 2, 3, 4].map((r) => {
    const o = byRound.get(r);
    return o != null ? (o.value || 0) : 0;
  });
  const skyScores = [1, 2, 3, 4].map((r) => {
    const o = byRound.get(r);
    if (!o) return '';
    return o.busts ? `${o.value}(${o.busts})` : `${o.value}`;
  });
  return { numericSkyScores, skyScores };
}

export const calculateRank = (teams, scores, bonusScores, bonusPuzzles) => {
  const teamsWithRank = teams.map(team => {
    const teamScores = scores.filter(s => s.teamId === team.id);
    const { numericSkyScores, skyScores } = skyRoundSlotsFromTeamScores(teamScores);

    // Sum all sky round values to get skySubtotal
    const skySubtotal = numericSkyScores.reduce((sum, val) => sum + val, 0);

    // Bonus scores (from bonusScores collection)
    const teamBonus = bonusPuzzles.map(puzzle => {
      const score = bonusScores.find(
        b => b.teamId === team.id && b.puzzleId === puzzle.id
      );
      return score ? score.value : 0;
    });

    const bonusSubtotal = teamBonus.reduce((sum, val) => sum + val, 0);
    const total = skySubtotal + bonusSubtotal;

    return {
      ...team,
      numericSkyScores,
      skyScores,            // For display only
      skySubtotal,
      bonusScores: teamBonus,
      bonusSubtotal,
      total,
    };
  });

  // Now do your custom multi-level sort
  teamsWithRank.sort((a, b) => {
    // 1) total desc
    if (b.total !== a.total) {
      return b.total - a.total;
    }
    // 2) skySubtotal desc
    if (b.skySubtotal !== a.skySubtotal) {
      return b.skySubtotal - a.skySubtotal;
    }
    // 3) compare rounds from 4 -> 1
    for (let roundIndex = 3; roundIndex >= 0; roundIndex--) {
      const diff = b.numericSkyScores[roundIndex] - a.numericSkyScores[roundIndex];
      if (diff !== 0) return diff; // if there's a difference, sort by it
    }
    // If we get here, everything is identical
    return 0;
  });

  // Assign ranks; teams that are fully "tied" share the same rank
  let rank = 1;
  let previous = null;

  const fullyTied = (current, prev) => {
    if (!prev) return false;
    if (current.total !== prev.total) return false;
    if (current.skySubtotal !== prev.skySubtotal) return false;
    // Check numeric sky scores in all 4 rounds
    for (let i = 0; i < 4; i++) {
      if (current.numericSkyScores[i] !== prev.numericSkyScores[i]) {
        return false;
      }
    }
    return true;
  };

  teamsWithRank.forEach((team, index) => {
    if (fullyTied(team, previous)) {
      // Same rank if fully tied
      team.rank = rank;
    } else {
      // Advance rank for a new distinct score
      rank = index + 1;
      team.rank = rank;
    }
    previous = team;
  });

  return teamsWithRank;
};
